<template>
  <div class="recurring-expense-details">
    <div class="table-wrapper clearfixing">
      <div class="header">
        <span class="link">{{ $lang.messages.recurringPayments.toUpperCase() }}</span>
        <div class="actions-btn">
          <img
            src="@/assets/images/payments/actions_ico.png"
            alt="kebab icon"
            @click="handleRecurringPayment"
          >
          <div :class="{'show-recurring-dropdown': handleRecurring}">
            <button class="generic-btn edit-expense" :class="{'disabled' : !changeable}">
              <router-link v-if="changeable" :to="{ name: computedRoute(expenseType), params: {id: guidChild}, query: {type: 'recurring'}}">
                <span>{{ $lang.messages.editExpense }}</span>
              </router-link>
              <span v-if="!changeable">
                {{ $lang.messages.editExpense }}
              </span>
            </button>
            <button
              class="generic-btn"
              :class="{'disabled' : !changeable}"
              @click="deleteModalOpen(guidChild, 'payment', 'deleteExpense')"
            >
              {{ $lang.messages.deleteExpense }}
            </button>
          </div>
        </div>
      </div>

      <!-- SPOUSAL/CHILD EXPENSE -->
      <div
          v-if="noExpenseTypeVal"
          class="section-row recurring-details-area"
      >
        <div class="recurring-details-data data-area">
          <div class="data-area-rows amount-row">
            <div class="recurring-data-columns">
              <div class="data-area-row amount">
                <span>{{ $lang.messages.amount }}</span>
                <span class="value special-value">
                  <currency-sign :currency="currency"></currency-sign>{{ currencyFormat(amount) }}
                </span>
              </div>
              <div class="data-area-row recipient">
                <span class="data-area-labels">{{ $lang.messages.startDate }}</span>
                <span class="data-area-values">{{ dateFormat(startDate) }}</span>
              </div>
              <div class="data-area-row payor">
                <span class="data-area-labels">{{ $lang.messages.endDate }}</span>
                <span class="data-area-values">
                  <span v-show="endDate !== null">{{ dateFormat(endDate) }}</span>
                  <span v-show="endDate === null" class="infinity-sign">∞</span>
                </span>
              </div>
              <div class="data-area-row">
                <span class="data-area-labels">{{ $lang.messages.scheduled }}</span>
                <span class="data-area-values">{{ scheduled }}</span>
              </div>
              <div class="data-area-row">
                <span class="data-area-labels">{{ $lang.messages.tp_autopayTitle }}</span>
                <span class="data-area-values" v-show="autopay">{{ $lang.messages.autopayEnabled }}</span>
                <span class="data-area-values" v-show="!autopay">{{ $lang.messages.autopayDisabled }}</span>
              </div>
            </div>
            <div class="recurring-data-columns">
              <div class="data-area-row">
                <span class="data-area-labels">{{ $lang.messages.submittedBy }}</span>
                <span class="data-area-values">{{ submittedBy.name }}</span>
              </div>
              <div class="data-area-row recipient">
                <span class="data-area-labels">{{ $lang.messages.recipient }}</span>
                <span class="data-area-values">{{ recipient.name }}</span>
              </div>
              <div class="data-area-row payor">
                <span class="data-area-labels">{{ $lang.messages.payor }}</span>
                <span class="data-area-values">{{ payor.name }}</span>
              </div>
              <div
                  v-if="expenseType === $CHILD_EXPENSE_TYPE"
                  class="data-area-row children"
              >
                <span class="data-area-labels">{{ $lang.messages.children }}</span>
                <span
                    v-for="(child, indexChild) in children"
                    :key='indexChild'
                    class="data-area-values"
                >
                  {{ child }}
                </span>
              </div>
            </div>
          </div>
          <div class="data-area-rows repeats-row">
            <div class="recurring-data-columns repeat-every">
              <div class="data-area-row">
                <span>{{ $lang.messages.repeatsEvery }}</span>
                <span class="value">{{ repeats }}</span>
              </div>
            </div>
            <div
                v-if="nextScheduledDate !== null"
                class="recurring-data-columns next-date"
            >
              <div class="data-area-row">
                <span>{{ $lang.messages.nextPaymentDate }}</span>
                <span class="value">{{ dateFormat(nextScheduledDate) }}</span>
              </div>
            </div>
          </div>
          <div class="data-area-rows notes-row" v-if="note !== null">
            <div class="data-area-row">
              <span class="data-area-labels">{{ $lang.messages.notePrivate }}</span>
              <span class="data-area-values">{{ note }}</span>
            </div>
          </div>

          <template v-if="attachments.length !== 0">
            <f-p-input-download
              :inputDownloadText="$lang.messages.download"
              :attachmentResponse="attachments"
              :isRecExpenseDetails="true"
            >
              <span>{{ $lang.messages.attachments }}</span>
            </f-p-input-download>
          </template>

        </div>
        <div
          v-if="list.length"
          v-show="!loading"
          class="recurring-details-data table-area"
        >
          <f-p-table-recurring-details
            :list="list"
            :paginationParams="paginationParams"
            :key="rerender"
            ref="recurringDetailsTable"
            @sort="sort"
            @onPaginationChanged="onPaginationChanged"
            @shareCheckedExpenses="shareCheckedExpenses">
          </f-p-table-recurring-details>
        </div>
        <div v-else class="text-center" v-show="!loading">
          <img src="@/assets/images/no-table-illustration.png" alt="money note" class="img-fluid">
        </div>
      </div>

      <!-- SHARED EXPENSE -->
      <div
        v-else
        class="section-row recurring-details-area"
      >
        <div class="recurring-details-data data-area">
          <div class="data-area-rows">
            <div class="data-area-row">
              <span class="data-area-labels">{{ $lang.messages.expenseDescription }}</span>
              <span class="data-area-values">{{ description }}</span>
            </div>
            <div class="data-area-row children">
              <span class="data-area-labels">{{ $lang.messages.children }}</span>
              <span
                v-for="(child, indexChildRec) in children"
                :key="indexChildRec"
                class="data-area-values"
              >
                {{ child }}
              </span>
            </div>
            <div class="data-area-row">
              <span class="data-area-labels">{{ $lang.messages.startDate }}</span>
              <span class="data-area-values">{{ dateFormat(startDate) }}</span>
            </div>
            <div class="data-area-row">
              <span class="data-area-labels">{{ $lang.messages.endDate }}</span>
              <span v-show="endDate !== null">{{ dateFormat(endDate) }}</span>
              <span v-show="endDate === null" class="infinity-sign">∞</span>
            </div>
            <div class="data-area-row">
              <span class="data-area-labels">{{ $lang.messages.scheduled }}</span>
              <span class="data-area-values">{{ scheduled }}</span>
            </div>
            <div class="data-area-row">
              <span class="data-area-labels">{{ $lang.messages.submittedBy }}</span>
              <span class="data-area-values">{{ submittedBy.name }}</span>
            </div>
            <div class="data-area-row">
              <span class="data-area-labels">{{ $lang.messages.tp_autopayTitle }}</span>
              <span class="data-area-values" v-show="autopay">{{ $lang.messages.autopayEnabled }}</span>
              <span class="data-area-values" v-show="!autopay">{{ $lang.messages.autopayDisabled }}</span>
            </div>
          </div>
          <div class="data-area-rows repeats-row">
            <div class="recurring-data-columns repeat-every">
              <div class="data-area-row">
                <span>{{ $lang.messages.repeatsEvery }}</span>
                <span class="value">{{ repeats }}</span>
              </div>
            </div>
            <div
                v-if="nextScheduledDate !== null"
                class="recurring-data-columns next-date"
            >
              <div class="data-area-row">
                <span>{{ $lang.messages.nextPaymentDate }}</span>
                <span class="value">{{ dateFormat(nextScheduledDate) }}</span>
              </div>
            </div>
          </div>
          <div class="data-area-rows notes-row" v-if="note !== null">
            <div class="data-area-row">
              <span class="data-area-labels">{{ $lang.messages.notePrivate }}</span>
              <span class="data-area-values">{{ note }}</span>
            </div>
          </div>

          <template v-if="attachments.length !== 0">
            <f-p-input-download
              :inputDownloadText="$lang.messages.download"
              :attachmentResponse="attachments"
              :isRecExpenseDetails="true"
            >
              <span>{{ $lang.messages.attachments }}</span>
            </f-p-input-download>
          </template>

        </div>
        <div class="recurring-details-data table-area">
          <div class="table-header clearfixing">
            <div class="half-section-row original-payment">
              <p class="payment-type uppercase-text">
                {{ $lang.messages.originalPaymentPart1 }}
                <br>
                {{ $lang.messages.originalPaymentPart2 }}
              </p>

              <div class="part">
                <div class="label">{{ $lang.messages.amount }}</div>
                <div class="info special-info">
                  <currency-sign :currency="currency"></currency-sign>{{ currencyFormat(amount) }}
                </div>
              </div>

              <div class="part coparent">
                <div class="label">{{ $lang.messages.paidBy }}</div>
                <div class="info">{{ payor.name }}</div>
              </div>
            </div>

            <div class="half-section-row reimbursement-payment">
              <p class="payment-type uppercase-text">{{ $lang.messages.reimbursementPayment }}</p>

              <div class="part">
                <div class="label">{{ $lang.messages.amount }}</div>
                <div class="info special-info">
                  <currency-sign :currency="currency"></currency-sign>{{ currencyFormat(reimbursementAmount) }}
                </div>
              </div>

              <div class="part coparent">
                <div class="label">{{ $lang.messages.owedBy }}</div>
                <div class="info">{{ recipient.name }}</div>
              </div>
            </div>

            <div class="section-row share-row">
              <span>
                {{ $lang.messages.yourShare }}:
                <currency-sign :currency="currency"></currency-sign>{{ currencyFormat(amount - reimbursementAmount) }} ({{ myShare }}%)
              </span>
               <span> / </span>
              <span>
                {{ $lang.messages.coparentsShare }}:
                <currency-sign :currency="currency"></currency-sign>{{ currencyFormat(reimbursementAmount) }} ({{ coparentsShare }}%)
              </span>

            </div>
          </div>
          <f-p-table-recurring-shared-details
            v-if="list.length > 0"
            :list="list"
            :paginationParams="paginationParams"
            :key="rerender"
            ref="sharedRecurringDetailsTable"
            @sort="sort"
            @onPaginationChanged="onPaginationChanged"
            @shareCheckedExpenses="shareCheckedExpenses">
          </f-p-table-recurring-shared-details>
        </div>
      </div>
    </div>

    <!-- Pay Expense modal -->
    <f-p-pay-expense-modal
      :nameProp="'payRecExpenseDetails'"
      ref="payRecExpenseDetails"
      @loadExpenses="getExpenseDetailsTableData">
    </f-p-pay-expense-modal>

    <!-- Pay Expense Total -->
    <f-p-pay-expense-total
      :total-number="totalNumber"
      :total-amount="totalAmount"
      :checkedExpensesAllProp="checkedExpensesAll"
      :sidebarExpandedProp="sidebarExpandedProp"
      @showTotalPayments="showModalRecTotalExpense">
    </f-p-pay-expense-total>

    <!-- Delete Modal -->
    <delete-modal
      ref="deleteModalComponent"
      @deleteExpense="deleteExpense">
    </delete-modal>
  </div>
</template>

<script>
  import { mapActions } from 'vuex';
  import {
    RECURRING_DELETE_SCHEDULED_EXPENSE,
    RECURRING_EXPENSES_DETAILS,
    RECURRING_EXPENSES_DETAILS_SHARED_TABLE,
    RECURRING_EXPENSES_DETAILS_TABLE
  } from '@/store/action.types';
  import { currencyFormat } from "@/mixins/currencyFormat";
  import { dateFormat } from "@/mixins/dateFormat";
  import CurrencySign from '@/components/common/CurrencySign';
  import FPPayExpenseModal from '@/components/common/modals/FPPayExpenseModal';
  import FPTableRecurringDetails from '@/components/common/tables/FPTableRecurringDetails';
  import FPTableRecurringSharedDetails from '@/components/common/tables/FPTableRecurringSharedDetails';
  import FPPayExpenseTotal from '@/components/common/FPPayExpenseTotal';
  import DeleteModal from "@/components/common/modals/DeleteModal";
  import FPInputDownload from '@/components/common/inputs/FPInputDownload';
  import STATUS_CODES from "@/common/statusCodes";

  export default {
    name: 'FpRecurringExpensesDetails',
    components: {
      CurrencySign,
      FPTableRecurringDetails,
      FPTableRecurringSharedDetails,
      FPPayExpenseTotal,
      FPPayExpenseModal,
      DeleteModal,
      'f-p-input-download': FPInputDownload
    },
    mixins: [
      currencyFormat,
      dateFormat
    ],
    props: {
      guid: {
        type: String,
        required: true,
        default: () => {
          return '';
        }
      },
      sidebarExpandedProp: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        expenseType: '',
        amount: 0,
        reimbursementAmount: '',
        description: '',
        currency: '',
        recipient: {
          guid: '',
          name: ''
        },
        payor: {
          guid: '',
          name: ''
        },
        repeats: '',
        scheduled: '',
        nextScheduledDate: new Date(),
        startDate: new Date(),
        endDate: new Date(),
        note: '',
        submittedBy: {
          guid: '',
          name: ''
        },
        myShare: 0,
        coparentsShare: 0,
        children: [],
        attachmentResponseDtos: [],
        scheduledExpenseGuid: '',
        changeable: false,
        // table
        list: [],
        sortParams: {
          orderProperty: "originalPayments.paymentDate",
          orderDirection: "ASC"
        },
        paginationParams: {
          pageNumber: 0,
          pageSize: 10,
          totalElements: 0,
          totalPages: 0
        },
        rerender: 0,
        totalNumber: 0,
        totalAmount: 0,
        checkedExpensesAll: [],
        payable: false,
        attachments: [],
        guidChild: this.guid,
        handleRecurring: false,
        autopay: false,
        loading: true,
      }
    },
    computed: {
      noExpenseTypeVal() {
        return this.expenseType !== this.$SHARED_EXPENSE_TYPE
      }
    },
    watch: {
      guidChild: function(val) {
        if (val !== '') {
          this.getExpense(val);
        }
      },
      $route: function (val) {
        this.guidChild = val.params.id;
      }
    },
    mounted() {
      if (this.guid !== "") {
        this.getExpense(this.guid);
      }
    },
    methods: {
      ...mapActions({
        getExpenseDetail: RECURRING_EXPENSES_DETAILS,
        getExpenseDetailTable: RECURRING_EXPENSES_DETAILS_TABLE,
        getExpenseDetailSharedTable: RECURRING_EXPENSES_DETAILS_SHARED_TABLE,
        deleteAction: RECURRING_DELETE_SCHEDULED_EXPENSE
      }),
      getExpense(guid) {
        this.loading = true;
        this.getExpenseDetail({
          "guid": guid
        })
        .then(response => {
          let data = response.data.data;
          this.amount = parseFloat(data.scheduledExpenseBasicResponseDto.amount).toFixed(2);
          this.currency = data.scheduledExpenseBasicResponseDto.currency;
          this.recipient = data.scheduledExpenseBasicResponseDto.recipient;
          this.payor = data.scheduledExpenseBasicResponseDto.payor;
          this.submittedBy = data.submittedBy;
          this.scheduled = data.scheduledExpenseBasicResponseDto.nextScheduledDate !== '' ? 'Yes' : 'No';
          this.repeats = data.scheduledExpenseBasicResponseDto.repeats;
          this.nextScheduledDate = data.scheduledExpenseBasicResponseDto.nextScheduledDate;
          this.startDate = data.scheduledExpenseBasicResponseDto.startDate;
          this.endDate = data.scheduledExpenseBasicResponseDto.endDate;
          this.note = data.scheduledExpenseBasicResponseDto.note
          this.attachments = data.attachmentResponseDtos;
          this.expenseType = data.scheduledExpenseBasicResponseDto.expenseType;
          if (data.scheduledExpenseBasicResponseDto.expenseType === this.$CHILD_EXPENSE_TYPE) {
            this.children = data.scheduledExpenseBasicResponseDto.children;
          }
          if (data.scheduledExpenseBasicResponseDto.expenseType === this.$SHARED_EXPENSE_TYPE) {
            this.reimbursementAmount = this.formatAmount(data.scheduledExpenseBasicResponseDto.reimbursementAmount);
            this.description = data.scheduledExpenseBasicResponseDto.description;
            this.children = data.scheduledExpenseBasicResponseDto.children;
            this.coparentsShare = Math.round(((100 * this.reimbursementAmount) / this.amount) * 100) / 100;
            this.myShare = Math.round((100 - this.coparentsShare) * 100) / 100;
          }
          this.changeable = data.scheduledExpenseBasicResponseDto.changeable
          this.autopay = data.autopayEnabled;

          this.getExpenseDetailsTableData();
        })
        .catch(error => {
          this.loading = false;
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        })
      },
      getExpenseDetailsTableData() {
        if(this.expenseType !== this.$SHARED_EXPENSE_TYPE) {
          this.getExpenseDetailTable({
            "expenseType": this.expenseType,
            "guid": this.guidChild,
            "sort": "",
            "page": this.paginationParams.pageNumber,
            "size": this.paginationParams.pageSize
          })
          .then(response => {
            response.data.data.content.forEach((item) => {
              item.expenseType = this.expenseType;
            });
            this.list = response.data.data.content;
            this.checkedExpensesAll = [];
            this.totalNumber = 0;
            this.totalAmount = 0;
            this.paginationParams.pageNumber = response.data.data.pageable.pageNumber;
            this.paginationParams.pageSize = response.data.data.pageable.pageSize;
            this.paginationParams.totalElements = response.data.data.totalElements;
            this.paginationParams.totalPages = response.data.data.totalPages;
            this.calculatePayable();
            this.loading = false;
            if (this.$refs.recurringDetailsTable)
              this.$refs.recurringDetailsTable.allChecked = false;
          })
          .catch(error => {
            this.loading = false;
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
            }
          })
        }
        else if (this.expenseType === this.$SHARED_EXPENSE_TYPE) {
          this.getExpenseDetailSharedTable({
            "expenseType": this.expenseType,
            "guid": this.guid,
            "sort": "",
            "page" : this.paginationParams.pageNumber,
            "size" : this.paginationParams.pageSize
          })
          .then(response => {
            let filteredList = [];
            response.data.data.content.forEach((item) => {
              item.expenseType = this.expenseType;
              filteredList.push(item);
            });
            this.list = filteredList;
            this.checkedExpensesAll = [];
            this.totalNumber = 0;
            this.totalAmount = 0;
            this.paginationParams.pageNumber = response.data.data.pageable.pageNumber;
            this.paginationParams.pageSize = response.data.data.pageable.pageSize;
            this.paginationParams.totalElements = response.data.data.totalElements;
            this.paginationParams.totalPages = response.data.data.totalPages;
            this.calculatePayable()
            if (this.$refs.sharedRecurringDetailsTable)
              this.$refs.sharedRecurringDetailsTable.allChecked = false;
          })
          .catch(error => {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
            }
          })
        }
      },
      calculatePayable() {
        let payable = true;
        this.list.forEach((item) => {
          if (!item.payable)
            payable = false
        });
        this.payable = payable
      },
      applyPagination(guid) {
        if(this.expenseType !== this.$SHARED_EXPENSE_TYPE) {
          this.getExpenseDetailTable({
            "expenseType": this.expenseType,
            "guid": guid,
            "sort": this.sortParams.orderProperty + "," + this.sortParams.orderDirection.toLowerCase(),
            "page": this.paginationParams.pageNumber,
            "size": this.paginationParams.pageSize
          })
          .then(response => {
            let filteredList = [];
            response.data.data.content.forEach((item) => {
              item.expenseType = this.expenseType;
              filteredList.push(item);
            });
            this.list = filteredList;
            this.checkedExpensesAll = [];
            this.totalNumber = 0;
            this.totalAmount = 0;
            this.paginationParams.pageNumber = response.data.data.pageable.pageNumber;
            this.paginationParams.pageSize = response.data.data.pageable.pageSize;
            this.paginationParams.totalElements = response.data.data.totalElements;
            this.paginationParams.totalPages = response.data.data.totalPages;
          })
          .catch(error => {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
            }
          })
        }
        else if(this.expenseType === this.$SHARED_EXPENSE_TYPE) {
          this.getExpenseDetailSharedTable({
            "expenseType": this.expenseType,
            "guid": guid,
            "sort": this.sortParams.orderProperty + "," + this.sortParams.orderDirection.toLowerCase(),
            "page" : this.paginationParams.pageNumber,
            "size" : this.paginationParams.pageSize
          })
          .then(response => {
            let filteredList = [];
            response.data.data.content.forEach((item) => {
              item.expenseType = this.expenseType;
              filteredList.push(item);
            });
            this.list = filteredList;
            this.checkedExpensesAll = [];
            this.totalNumber = 0;
            this.totalAmount = 0;
            this.paginationParams.pageNumber = response.data.data.pageable.pageNumber;
            this.paginationParams.pageSize = response.data.data.pageable.pageSize;
            this.paginationParams.totalElements = response.data.data.totalElements;
            this.paginationParams.totalPages = response.data.data.totalPages;
          })
          .catch(error => {
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
            }
          })
        }
      },
      formatAmount(data) {
        if (data !== '') {
          return parseFloat(data).toFixed(2);
        }
      },
      sort(sortParam, sortDir) {
        if (sortParam !== "") {
          this.sortParams.orderProperty = sortParam;
          this.sortParams.orderDirection = sortDir;
          this.applyPagination(this.guidChild);
        }
      },
      onPaginationChanged(paginationParams) {
        this.paginationParams = paginationParams;
        this.applyPagination(this.guidChild);
      },
      deleteModalOpen(guid, type, typeOfDeletion) {
        this.$refs.deleteModalComponent.show(guid, type, typeOfDeletion);
      },
      deleteExpense() {
        this.deleteAction(this.guidChild)
        .then(() => {
          this.$snotify.success(this.$lang.messages.sn_paymentSuccessDelete, 'SUCCESS');
          this.$router.push({ name: "Recurring" });
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(error.response.data.status.desc, 'ERROR');
          }
        });
      },
      shareCheckedExpenses(expense) {
        if (expense.isChecked) {
          if (this.checkedExpensesAll.indexOf(expense) === -1) {
            this.checkedExpensesAll.push(expense)
          }
        } else {
          this.checkedExpensesAll = this.removeExpense(expense, this.checkedExpensesAll)
        }
        this.calculateTotalExpenses()
      },
      removeExpense(element, expenses) {
        return expenses.filter(elem => elem.expenseGuid !== element.expenseGuid)
      },
      calculateTotalExpenses() {
        this.totalNumber = 0;
        this.totalAmount = 0;
        if (this.checkedExpensesAll.length > 0) {
          this.checkedExpensesAll.forEach(element => {
            if (element.status === 'NOT_PAID') {
              this.totalNumber += 1;
              this.totalAmount = this.totalAmount + element.originalAmount;
            }
          });
        } else {
          this.totalNumber = 0;
          this.totalAmount = 0;
        }
      },
      // Click on individial Recurring Expense inside table
      showPayExpenseModal() {
        this.$refs.payRecExpenseDetails.show(this.checkedExpensesAll)
      },
      // Clicking on Total pay button
      showModalRecTotalExpense() {
        this.$refs.payRecExpenseDetails.show(this.checkedExpensesAll);
      },
      computedRoute(expenseType) {
        return (expenseType === this.$ALIMONY_EXPENSE_TYPE ?
          'ScheduleRecurringPaymentSpousalMaintenance' : expenseType === this.$CHILD_EXPENSE_TYPE ?
          'ScheduleRecurringPaymentChildMaintenance' : 'ScheduleRecurringPaymentSharedExpenses');
      },
      handleRecurringPayment () {
        this.handleRecurring = !this.handleRecurring
      }
    }
  }
</script>
